// @ts-strict-ignore
import { captureException } from '@sentry/nextjs';
import actionTypes from 'src/modules/community/communityPosts/actionTypes';
import { get, getPublic, getServerSideConfig } from 'src/utils/api';
import { setAlert } from 'src/components/alert/actions';
import postsFilterOptions from 'src/constants/postsFilterOptions';
import { Lang } from 'src/constants/types';
import { MAX_POSTS_WIDGET } from 'src/constants/widgets';
import { ReduxState } from 'src/store/store';

export const fetchPosts = ({
  communitySlug,
  filter = 'latest',
  topicId = null,
  topicName = '',
  isSeeMoreClick = false,
  year = null,
  month = null,
  pageNumber = null,
}: {
  communitySlug: string;
  filter?: string;
  topicId?: number;
  topicName?: string;
  isSeeMoreClick?: boolean;
  year?: string;
  month?: string;
  pageNumber?: number;
})  => {
  const postsFilter = postsFilterOptions.find(({ value }) => value === filter) ? filter : 'latest';

  return async (dispatch, getState: () => ReduxState) => {
    const state = getState();
    const previousFilter = state.pages.communityPosts.filter;

    dispatch({
      type: actionTypes.FETCH_COMMUNITY_POSTS_PAGE_POSTS,
      archiveCurrentMonth: year ? month : null,
      archiveCurrentYear: year,
      filter: postsFilter,
      isSeeMoreClick,
      selectedTopic: topicName,
      selectedTopicId: topicId,
    });

    try {
      const url = `posts/${communitySlug}/${postsFilter}`;
      const createdBeforePostId = isSeeMoreClick && ['active', 'popular', 'pinned'].indexOf(filter) < 0
        ? state.pages.communityPosts.lastPostId
        : null;

      const currentPageNumber = state.pages.communityPosts.currentPageNumber;
      let newPageNumber = currentPageNumber;
      // pageNumber will be null each time fetchPosts() is called, except on archive page with page-number param in URL
      if (year) {
        // For archive (should be value of page-number param or 1 if param doesn't exist or is not a number)
        newPageNumber = pageNumber ? pageNumber : 1;
      } else if (!currentPageNumber || filter !== previousFilter) {
        // On page load or changing filter
        newPageNumber = 1;
      } else if (isSeeMoreClick) {
        newPageNumber = currentPageNumber + 1;
      }
      // pageNumber param only for archive page and for active, popular and pinned filters
      let pageNumberParam = null;
      if (year || isSeeMoreClick && ['active', 'popular', 'pinned'].indexOf(filter) >= 0) {
        pageNumberParam = newPageNumber;
      }
      // Params need to be null and not empty strings, or the API request URL will have them
      // and we get a 400 bad request error
      const params = {
        topicId,
        createdBeforePostId, // filters latest/unanswered
        year, // archive page
        month: year ? month : null, // archive page
        pageNumber: pageNumberParam, // archive page & filters active/popular/pinned
      };

      const { data: posts } = !state.context.currentUser?.isCommunityMemberOrHUStaff
        ? await getPublic(url, { params })
        : await get(`private/${url}`, { params, ...getServerSideConfig(state.context.sessionId) });

      dispatch({
        type: actionTypes.FETCH_COMMUNITY_POSTS_PAGE_POSTS_SUCCESS,
        currentPageNumber: pageNumberParam !== null ? pageNumberParam : newPageNumber,
        hasMorePosts: posts.length >= 30,
        isSeeMoreClick,
        posts,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.FETCH_COMMUNITY_POSTS_PAGE_POSTS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchCommunityTopTags = (communityId: number, params?: { limit: number; language: Lang; }) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_COMMUNITY_TOP_TAGS });
    try {
      const { data } = await getPublic(`community-top-tags/${communityId}`, { params });
      dispatch({
        type: actionTypes.FETCH_COMMUNITY_TOP_TAGS_SUCCESS,
        topTags: data,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.FETCH_COMMUNITY_TOP_TAGS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchLatestPoll = (communitySlug: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.FETCH_LATEST_POLL });
    try {
      const state = getState();
      const url = `posts/${communitySlug}/latest-poll`;
      const { data: posts } = !state.context.currentUser?.isCommunityMemberOrHUStaff
        ? await getPublic(url)
        : await get(`private/${url}`, getServerSideConfig(state.context.sessionId));
      dispatch({
        type: actionTypes.FETCH_LATEST_POLL_SUCCESS,
        latestPoll: posts,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_LATEST_POLL_FAILURE });
      captureException(ex);
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchPinnedPosts = (communitySlug: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.FETCH_POSTS_PINNED_POSTS });
    try {
      const state = getState();
      const url = `posts/${communitySlug}/pinned`;

      const { data } = !state.context.currentUser?.isCommunityMemberOrHUStaff
        ? await getPublic(url)
        : await get(`private/${url}`, getServerSideConfig(state.context.sessionId));

      const pinnedPosts = data.slice(0, MAX_POSTS_WIDGET).map(post => ({
        postId: post.postId,
        title: post.title,
        userImageHash: post.author.userImageHash,
        username: post.author.username,
      }));
      dispatch({
        type: actionTypes.FETCH_POSTS_PINNED_POSTS_SUCCESS,
        pinnedPosts,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.FETCH_POSTS_PINNED_POSTS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchArchive = (communitySlug: string) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_ARCHIVE });
    try {
      const { data } = await getPublic(`posts/${communitySlug}/archive`);
      dispatch({
        type: actionTypes.FETCH_ARCHIVE_SUCCESS,
        archiveData: data,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_ARCHIVE_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchTotalMonthlyPosts = (communitySlug: string, year: string, month: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.FETCH_TOTAL_MONTHLY_POSTS });
    try {
      const state = getState();
      const url = `posts/${communitySlug}/total`;
      const params = { year, month };
      const { data: { totalPosts } } = !state.context.currentUser?.isCommunityMemberOrHUStaff
        ? await getPublic(url, { params })
        : await get(`private/${url}`, { params, ...getServerSideConfig(state.context.sessionId) });
      dispatch({
        type: actionTypes.FETCH_TOTAL_MONTHLY_POSTS_SUCCESS,
        totalMonthlyPosts: totalPosts,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_TOTAL_MONTHLY_POSTS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};
